import { createColumnHelper } from '@tanstack/react-table'
import type { Order } from '../types/orders.types'
import { StatusBadge } from '../../../shared/components'
import { Eye } from 'lucide-react'
import { TotalCell } from '../components/TotalCell'
import { formatOrderDate } from '../../../shared/utils/utils'

const columnHelper = createColumnHelper<Order>()

export const createOrderColumns = (onViewDetails: (order: Order) => void, merchantMap?: Map<string, string>) => {
  const cols = [
  columnHelper.accessor((row) => row.friendlyOrderId ?? row.orderId, {
    id: 'orderId',
    header: 'Order ID',
    enableSorting: true,
    cell: (info) => (
      <span
        className="font-semibold text-[16px] font-mono"
        style={{
          lineHeight: '140%',
          color: '#3F3E42',
        }}
        title={info.getValue()}
      >
        {info.getValue() || '—'}
      </span>
    ),
  }),
  columnHelper.accessor('date', {
    header: 'Date',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const dateA = new Date(rowA.original.date)
      const dateB = new Date(rowB.original.date)
      
      // Handle invalid dates in sorting
      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0
      if (isNaN(dateA.getTime())) return 1
      if (isNaN(dateB.getTime())) return -1
      
      return dateA.getTime() - dateB.getTime()
    },
    cell: (info) => {
      const dateStr = info.getValue()
      
      // Handle empty or null dates
      if (!dateStr) {
        return <span className="text-muted-foreground">No date</span>
      }
      
      const date = new Date(dateStr)
      
      // Handle invalid dates
      if (isNaN(date.getTime())) {
        return <span className="text-red-500">Invalid Date</span>
      }
      
      return (
        <span 
          className="font-semibold text-[16px]"
          style={{
            lineHeight: '140%',
            letterSpacing: '0%',
            background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          {formatOrderDate(date)}
        </span>
      )
    },
  }),
  columnHelper.accessor('customer.name', {
    header: 'Customer',
    enableSorting: true,
    cell: (info) => (
      <div className="max-w-[200px] h-[48px] flex flex-col gap-[4px] pr-4">
        <span
          className="font-semibold text-[16px] truncate"
          style={{
            lineHeight: '140%',
            letterSpacing: '0%',
            background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
          title={info.getValue()}
        >
          {info.getValue()}
        </span>
        <span
          className="font-normal text-[16px] text-muted-foreground truncate"
          style={{
            lineHeight: '140%',
            letterSpacing: '0%'
          }}
          title={info.row.original.customer.email}
        >
          {info.row.original.customer.email}
        </span>
      </div>
    ),
  }),
  columnHelper.accessor('items', {
    header: 'Items',
    cell: (info) => (
      <div className="max-w-[180px] flex flex-col gap-[4px] pr-4">
        <div className="flex items-center gap-[6px]">
          <span
            className="font-semibold text-[16px]"
            style={{
              lineHeight: '140%',
              letterSpacing: '0%',
              background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {info.getValue().count} item(s)
          </span>
          {info.row.original.orderType === 'BESPOKE' && (
            <span
              className="inline-block px-[6px] py-[2px] rounded-[4px] text-[11px] font-semibold flex-shrink-0"
              style={{ backgroundColor: '#E2DEF7', color: '#28282A' }}
            >
              Bespoke
            </span>
          )}
        </div>
        <span
          className="font-normal text-[16px] text-muted-foreground truncate"
          style={{
            lineHeight: '140%',
            letterSpacing: '0%'
          }}
          title={info.getValue().description}
        >
          {info.getValue().description}
        </span>
      </div>
    ),
  }),
  columnHelper.accessor('total', {
    header: 'Total',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      return rowA.original.totalAmount - rowB.original.totalAmount
    },
    cell: (info) => (
      <TotalCell amount={info.row.original.totalAmount} />
    ),
  }),
  columnHelper.accessor('payment', {
    header: 'Payment',
    enableSorting: true,
    cell: (info) => {
      const payment = info.getValue()
      const colorMap = {
        // 'Paid': 'bg-green-100 text-green-800',
        'Pending': 'bg-yellow-100 text-yellow-800',
        'Failed': 'bg-red-100 text-red-800',
        'Refunded': 'bg-muted text-foreground',
        "Paid": 'bg-green-100 text-green-800',
      }
      return (
        <span
          className={`w-fit px-2 py-1 rounded-full text-[14px] font-medium ${colorMap[payment] || 'bg-muted text-foreground'}`}
        >
          {payment}
        </span>
      )
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    enableSorting: true,
    cell: (info) =>
      <StatusBadge status={info.getValue()} />
  }),
  columnHelper.display({
    id: 'tracking',
    header: 'Tracking',
    cell: ({ row }) => {
      const { dhlTrackingNumber, carrier, manualTrackingNumber, shippingMethod } = row.original
      const trackingNumber = dhlTrackingNumber || manualTrackingNumber
      const carrierLabel = dhlTrackingNumber ? 'DHL' : (carrier || shippingMethod || null)

      if (!trackingNumber) {
        return (
          <span className="text-muted-foreground font-semibold text-[16px]">—</span>
        )
      }

      return (
        <div className="flex flex-col gap-[2px]">
          {carrierLabel && (
            <span
              className="font-normal text-[12px] text-muted-foreground"
              style={{ lineHeight: '140%' }}
            >
              {carrierLabel}
            </span>
          )}
          <span
            className="font-semibold text-[14px] font-mono"
            style={{
              lineHeight: '140%',
              color: '#3F3E42',
            }}
            title={trackingNumber}
          >
            {trackingNumber}
          </span>
        </div>
      )
    },
  }),
  columnHelper.accessor('location', {
    header: 'Location',
    cell: (info) => (
      <span 
        className="font-semibold text-[16px] w-fit"
        style={{
          lineHeight: '140%',
          letterSpacing: '0%',
          background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Details',
    enableSorting: false,
    cell: ({ row }) => {
      const handleClick = () => {
        onViewDetails(row.original);
      };

      return (
        <button 
          className="w-[48px] h-[40px] rounded-[10px] border border-[#B8B5B6] flex items-center justify-center hover:bg-muted transition-colors"
          onClick={handleClick}
        >
          <Eye 
            size={18}
            className="text-foreground"
            strokeWidth={1.5}
          />
        </button>
      );
    },
  }),
  ];

  if (merchantMap) {
    cols.splice(3, 0, columnHelper.accessor('merchantId', {
      header: 'Merchant',
      enableSorting: true,
      cell: (info) => (
        <span 
          className="font-semibold text-[16px]"
          style={{
            lineHeight: '140%',
            color: '#3F3E42',
          }}
        >
          {merchantMap.get(info.getValue()) || '—'}
        </span>
      ),
    }) as (typeof cols)[number]);
  }

  return cols;
}